<template>
  <HrPageLayout
    title="Apply for Off Day"
    subtitle="My Leave"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'My Leave', route: '/employee-off-day-history' },
      { label: 'Apply for Off Day' }
    ]"
  >
    <div class="row">
      <div v-if="!allowed" class="col-12 mb-3">
        <div class="alert alert-warning mb-0">
          {{ restrictMessage || 'Apply for Off Day is only available to staff of Public Health & Medical Services.' }}
        </div>
      </div>
      <div class="col-12 mb-3" v-if="allowed">
        <div class="card bg-light">
          <div class="card-body">
            <h5 class="mb-3"><i class="mdi mdi-calendar-star text-primary me-2"></i>Your assigned off day programs</h5>
            <div v-if="loadingPrograms" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary"></div>
            </div>
            <div v-else-if="!programs.length" class="text-muted">
              No off day programs are assigned to you. Contact HR if you believe this is an error.
            </div>
            <div v-else class="row">
              <div v-for="p in programs" :key="p.id" class="col-md-4 col-sm-6 mb-2">
                <div class="p-3 bg-white rounded shadow-sm h-100">
                  <strong>{{ p.program_name }}</strong>
                  <p class="small text-muted mb-1">{{ p.description || 'Off day program' }}</p>
                  <span class="badge bg-primary">{{ p.remainingDays }} of {{ p.maxDays }} day(s) remaining</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-8" v-if="allowed">
        <div class="card">
          <div class="card-header bg-primary text-white">
            <h5 class="card-title text-white mb-0">Off Day Application</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="submitRequest">
              <div class="mb-3">
                <label class="form-label">Off day program <span class="text-danger">*</span></label>
                <select v-model="form.programId" class="form-select" required @change="onProgramChange">
                  <option value="">Select program</option>
                  <option v-for="p in programs" :key="p.id" :value="p.id" :disabled="p.remainingDays <= 0">
                    {{ p.program_name }} ({{ p.remainingDays }} remaining)
                  </option>
                </select>
              </div>

              <div v-if="selectedProgram && selectedProgram.allow_half_day == 1" class="mb-3">
                <label class="form-label">Duration</label>
                <div class="d-flex gap-3">
                  <label class="form-check">
                    <input v-model="durationType" class="form-check-input" type="radio" value="full">
                    <span class="form-check-label">Full day(s)</span>
                  </label>
                  <label class="form-check">
                    <input v-model="durationType" class="form-check-input" type="radio" value="half">
                    <span class="form-check-label">Half day</span>
                  </label>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Start date <span class="text-danger">*</span></label>
                  <input v-model="form.startDate" type="date" class="form-control" required>
                </div>
                <div class="col-md-6">
                  <label class="form-label">End date <span class="text-danger">*</span></label>
                  <input v-model="form.endDate" type="date" class="form-control" :disabled="durationType === 'half'" required>
                </div>
              </div>

              <div v-if="durationType === 'half'" class="mb-3">
                <label class="form-label">Session</label>
                <select v-model="form.halfDaySession" class="form-select" required>
                  <option value="">Select session</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">Reason <span class="text-danger">*</span></label>
                <textarea v-model="form.reason" class="form-control" rows="3" required placeholder="Reason for off day"></textarea>
              </div>

              <button type="submit" class="btn btn-primary" :disabled="submitting || !programs.length">
                {{ submitting ? 'Submitting...' : 'Submit request' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from '@/components/hrActions/HrPageLayout.vue';
import { authUrl, execute } from '@/api';

export default {
  name: 'EmployeeOffDayApply',
  components: { HrPageLayout },
  data() {
    return {
      loadingPrograms: false,
      submitting: false,
      programs: [],
      allowed: true,
      restrictMessage: '',
      durationType: 'full',
      form: {
        programId: '',
        startDate: '',
        endDate: '',
        halfDaySession: '',
        reason: ''
      }
    };
  },
  computed: {
    selectedProgram() {
      return this.programs.find(p => String(p.id) === String(this.form.programId));
    }
  },
  mounted() {
    this.loadPrograms();
  },
  methods: {
    async loadPrograms() {
      this.loadingPrograms = true;
      const data = new FormData();
      data.append('function', 'getEmployeeOffDayPrograms');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.programs = res.data.data?.programs || [];
          this.allowed = res.data.data?.allowed !== false;
          this.restrictMessage = res.data.data?.message || '';
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingPrograms = false;
      }
    },
    onProgramChange() {
      if (this.selectedProgram && this.selectedProgram.allow_half_day != 1) {
        this.durationType = 'full';
      }
    },
    async submitRequest() {
      if (!this.form.programId) return;
      if (this.durationType === 'half') {
        this.form.endDate = this.form.startDate;
      }
      this.submitting = true;
      const data = new FormData();
      data.append('function', 'applyForOffDay');
      data.append('programId', this.form.programId);
      data.append('startDate', this.form.startDate);
      data.append('endDate', this.form.endDate);
      data.append('reason', this.form.reason);
      data.append('durationType', this.durationType);
      if (this.durationType === 'half') {
        data.append('halfDaySession', this.form.halfDaySession);
      }
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          alert(res.data.message || 'Submitted successfully');
          this.form = { programId: '', startDate: '', endDate: '', halfDaySession: '', reason: '' };
          this.durationType = 'full';
          this.loadPrograms();
        } else {
          alert(res.data.message || 'Submission failed');
        }
      } catch (e) {
        alert('Submission failed');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

